import React from 'react';
import { Card, CardContent } from "@/ui";
import { FileText, Shield, User, Key, AlertTriangle, Stethoscope, Lock, Lightbulb, Link2, CreditCard, Ban, AlertCircle, Scale, Gavel, Mail } from 'lucide-react';

const PRIMARY_BLUE = "#1356a6";

export default function TermsOfService() {
  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[84rem] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-blue-50 shadow-sm">
                <FileText className="h-8 w-8" style={{ color: PRIMARY_BLUE }} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-normal mb-4" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Terms of Service
            </h1>
            <p className="text-lg text-gray-600" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              <strong>Effective Date:</strong> January 8, 2026
            </p>
          </div>

          {/* Introduction */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                These Terms of Service ("Terms") govern your access to and use of the website, software platforms, and services (collectively, the "Services") provided by <strong>AIscendus Inc</strong> ("AIscendus," "Company," "we," "our," or "us"). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, do not use the Services.
              </p>
            </CardContent>
          </Card>

          {/* Section 1: Description of Services */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  1. Description of Services
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus provides electronic medical record (EMR) software and healthcare technology solutions intended for use by healthcare organizations and authorized professionals. The Services are offered as a software-as-a-service (SaaS) platform and may include website access, hosted applications, APIs, integrations, and related support services.
              </p>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                The Services are <strong>not intended for use by patients directly</strong> unless expressly authorized by a healthcare provider.
              </p>
            </CardContent>
          </Card>

          {/* Section 2: Eligibility and Authority */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <User className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  2. Eligibility and Authority
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                You represent and warrant that:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>You are at least 18 years old; and</li>
                <li>You have the legal authority to enter into these Terms on behalf of yourself or the organization you represent.</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700 mt-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                If you are using the Services on behalf of an organization, that organization is responsible for all actions taken through your account.
              </p>
            </CardContent>
          </Card>

          {/* Section 3: Accounts and Access */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Key className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  3. Accounts and Access
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                To access certain Services, you may be required to create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Provide accurate and complete information</li>
                <li>Maintain the confidentiality of login credentials</li>
                <li>Notify AIscendus immediately of any unauthorized access or security breach</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                You are responsible for all activity that occurs under your account.
              </p>
            </CardContent>
          </Card>

          {/* Section 4: Acceptable Use */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <AlertTriangle className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  4. Acceptable Use
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Use the Services in violation of any applicable laws or regulations</li>
                <li>Attempt to gain unauthorized access to systems or data</li>
                <li>Interfere with the integrity or performance of the Services</li>
                <li>Introduce malware, viruses, or harmful code</li>
                <li>Reverse engineer, decompile, or attempt to extract source code except as permitted by law</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus reserves the right to suspend or terminate access for violations of this section.
              </p>
            </CardContent>
          </Card>

          {/* Section 5: Healthcare Compliance and Responsibilities */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Stethoscope className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  5. Healthcare Compliance and Responsibilities
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Customers using the Services are solely responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Compliance with applicable healthcare laws and regulations, including HIPAA</li>
                <li>The accuracy, quality, and legality of data entered into the Services</li>
                <li>Obtaining all necessary patient consents and authorizations</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus acts as a technology provider and does not provide medical advice or clinical decision-making services.
              </p>
            </CardContent>
          </Card>

          {/* Section 6: Protected Health Information (PHI) */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Lock className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  6. Protected Health Information (PHI)
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                To the extent AIscendus processes Protected Health Information (PHI), such processing is governed by applicable <strong>Business Associate Agreements (BAAs)</strong> entered into with customers. In the event of a conflict between these Terms and a BAA, the BAA shall control.
              </p>
            </CardContent>
          </Card>

          {/* Section 7: Intellectual Property */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  7. Intellectual Property
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                All intellectual property rights in the Services, including software, documentation, trademarks, and logos, are owned by or licensed to AIscendus. These Terms do not grant you any ownership rights.
              </p>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                You are granted a limited, non-exclusive, non-transferable, revocable license to use the Services solely for your internal business purposes during the term of your agreement with AIscendus.
              </p>
            </CardContent>
          </Card>

          {/* Section 8: Feedback */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Lightbulb className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  8. Feedback
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                If you provide suggestions, feedback, or ideas regarding the Services, you grant AIscendus a perpetual, irrevocable, royalty-free license to use such feedback without restriction or compensation.
              </p>
            </CardContent>
          </Card>

          {/* Section 9: Third-Party Services */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Link2 className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  9. Third-Party Services
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                The Services may integrate with third-party products or services. AIscendus is not responsible for third-party services and does not warrant their availability, security, or performance.
              </p>
            </CardContent>
          </Card>

          {/* Section 10: Fees and Payment */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <CreditCard className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  10. Fees and Payment
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Fees, payment terms, and billing arrangements are governed by separate written agreements or order forms between AIscendus and the customer. Failure to pay applicable fees may result in suspension or termination of Services.
              </p>
            </CardContent>
          </Card>

          {/* Section 11: Suspension and Termination */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Ban className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  11. Suspension and Termination
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus may suspend or terminate access to the Services:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>For violation of these Terms</li>
                <li>For non-payment of fees</li>
                <li>To protect the security or integrity of the Services</li>
                <li>As required by law</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Upon termination, your right to use the Services will cease.
              </p>
            </CardContent>
          </Card>

          {/* Section 12: Disclaimers */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <AlertCircle className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  12. Disclaimers
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." AIscendus DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus does not warrant that the Services will be uninterrupted, error-free, or free from security vulnerabilities.
              </p>
            </CardContent>
          </Card>

          {/* Section 13: Limitation of Liability */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Scale className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  13. Limitation of Liability
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AIscendus SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS INTERRUPTION.
              </p>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus's total liability arising out of or relating to the Services shall not exceed the amount paid by the customer to AIscendus in the twelve (12) months preceding the claim.
              </p>
            </CardContent>
          </Card>

          {/* Section 14: Indemnification */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  14. Indemnification
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                You agree to indemnify and hold harmless AIscendus from any claims, damages, losses, or expenses arising from:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Your use of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of applicable laws or regulations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 15: Governing Law and Venue */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Gavel className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  15. Governing Law and Venue
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                These Terms are governed by and construed in accordance with the laws of the State of [Insert State], without regard to conflict of law principles.
              </p>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Any legal action shall be brought exclusively in the state or federal courts located within [Insert County and State].
              </p>
            </CardContent>
          </Card>

          {/* Section 16: Changes to These Terms */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  16. Changes to These Terms
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus may update these Terms from time to time. Continued use of the Services after changes become effective constitutes acceptance of the revised Terms.
              </p>
            </CardContent>
          </Card>

          {/* Section 17: Contact Information */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Mail className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  17. Contact Information
                </h2>
              </div>
              <div className="space-y-2" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p className="text-base font-semibold text-gray-900">
                  <strong>AIscendus Inc</strong>
                </p>
                <p className="text-base text-gray-700">
                  Email: <a href="mailto:info@aiscendus.ai" className="text-blue-600 hover:underline">info@aiscendus.ai</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

